/* eslint-env es2020 */
import React, { useEffect } from 'react';
import './Validator.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import reddickPointingRight from '../../../assets/reddickpointingright.png';

const Validator = () => {
  const validatorAddress = 'validator_rdx1swywqhv3ed5w6dldfs2vamtrsyrdsfd6l9cqen5xqe54597gyckpns';
  const extraReddicksApyPercent = 15;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Define static APY ranges
  const baseApyMin = 5.80;
  const baseApyMax = 6.20;
  const effectiveApyMin = baseApyMin + (baseApyMin * (extraReddicksApyPercent / 100));
  const effectiveApyMax = baseApyMax + (baseApyMax * (extraReddicksApyPercent / 100));

  const baseApyRangeString = `${baseApyMin.toFixed(2)}% - ${baseApyMax.toFixed(2)}%`;
  const effectiveApyRangeString = `${effectiveApyMin.toFixed(2)}% - ${effectiveApyMax.toFixed(2)}%`;

  return (
    <div className="validator-page">
      <Header />
      <main className="validator-content">
        {/* Hero Section */}
        <section className="validator-hero-section">
          <div className="container">
            <h1>Support Reddicks & Radix</h1>
            <p className="subtitle">Stake Your XRD with the Official REDDICKS Validator and Earn Extra $DCKS Rewards!</p>
            <a
              href={`https://dashboard.radixdlt.com/network-staking/${validatorAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button validator-cta pulse-button"
            >
              Stake XRD Now
            </a>
          </div>
        </section>

        {/* What is a Validator Section */}
        <section className="about-validator-section">
          <div className="container">
            <h2>What's a Validator & Why Stake?</h2>
            <div className="about-content">
              <div className="about-image">
                <img src={reddickPointingRight} alt="Reddick pointing right explaining validators" />
              </div>
              <div className="about-text">
                <p>On the Radix network, Validators are the backbone of security and operation. They run specialized servers (nodes) responsible for processing transactions, proposing new blocks of transactions, and maintaining the integrity of the distributed ledger. Think of them as the trusted guardians ensuring the network runs smoothly and securely.</p>
                <p>When you "stake" your XRD tokens to a validator, you're essentially delegating your voting power to them. This signifies your trust in that validator and strengthens their ability to participate in Radix's Proof-of-Stake consensus mechanism – the process by which the network agrees on the valid order of transactions.</p>
                <p><strong>Why does staking matter?</strong> By staking your XRD, you actively contribute to the decentralization and security of the Radix network. A widely distributed stake across many validators makes the network incredibly resilient against attacks. As a thank you for your contribution, you earn staking rewards!</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Stake with REDDICKS Section */}
        <section className="why-reddicks-section">
          <div className="container">
            <h2>Boost Your Staking Rewards with REDDICKS</h2>
            <p className="section-intro">Choosing the REDDICKS validator means you contribute to Radix security while maximizing your rewards, paid entirely in $DCKS!</p>

            <div className="rewards-highlight">
              <div className="apy-display">
                <div className="apy-label">Estimated Effective APY Range (Paid in $DCKS)</div>
                <div className="apy-value">{effectiveApyRangeString}*</div>
                <div className="apy-breakdown">
                  (Value of ~{baseApyRangeString} XRD APY + {extraReddicksApyPercent}% Bonus)
                </div>
              </div>
              <p className="apy-note">
                *Due to the 100% validator fee (which funds $DCKS buybacks!), you won't receive direct XRD rewards.
                Instead, you receive the <strong>full value</strong> equivalent to the standard XRD APY (typically between {baseApyRangeString}, subject to network changes)
                <strong> plus an extra {extraReddicksApyPercent}% bonus on top</strong>. This total reward value is paid out monthly in REDDICKS tokens from our dedicated reward fund. Check the Radix Explorer for the precise current base APY.
              </p>
            </div>

            <div className="why-grid">
              {/* Card 1 */}
              <div className="why-card">
                <div className="why-icon rewards"></div>
                <h3>Earn More with $DCKS</h3>
                <p>Get the full value of standard XRD staking rewards <strong>plus a {extraReddicksApyPercent}% bonus</strong>, all paid monthly in $DCKS. Funded by 15M XRD staked permanently by the team (not the treasury).</p>
              </div>
              {/* Card 2 */}
              <div className="why-card">
                <div className="why-icon support"></div>
                <h3>Fuel the $DCKS Ecosystem</h3>
                <p>The 100% validator fee collected in XRD goes directly to the REDDICKS treasury. These funds are used to buy back $DCKS tokens, creating consistent buying pressure and supporting the ecosystem.</p>
              </div>
              {/* Card 3 */}
               <div className="why-card">
                 <div className="why-icon transparent"></div>
                <h3>Transparent & Reliable</h3>
                <p>Your $DCKS rewards (representing the XRD APY value + bonus) are paid reliably on the first Monday of each month. The team's 15M XRD stake ensures long-term funding. Track validator performance via the Radix Explorer.</p>
              </div>
              {/* Card 4 */}
               <div className="why-card">
                 <div className="why-icon easy"></div>
                <h3>Simple & Secure Staking</h3>
                <p>Delegate your XRD easily and securely using the official Radix Wallet. Your tokens always remain in your control. Contribute to Radix network security while maximizing your returns.</p>
              </div>
            </div>

            <div className="validator-address-info">
              <h3>Our Validator Address:</h3>
              <p className="address-text">{validatorAddress}</p>
              <a
                href={`https://dashboard.radixdlt.com/network-staking/${validatorAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="verify-link"
              >
                Verify & Stake on Radix Explorer
              </a>
            </div>
          </div>
        </section>

        {/* How to Stake Section */}
        <section className="how-to-stake-section">
            <div className="container">
                <h2>How to Stake Your XRD</h2>
                <div className="steps-container">
                    <div className="step">
                        <div className="step-number">1</div>
                        <div className="step-content">
                            <h3>Get the Radix Wallet</h3>
                            <p>If you haven't already, download and set up the official Radix Wallet on your mobile device. Remember to securely back up your seed phrase!</p>
                            <a href="https://wallet.radixdlt.com/" target="_blank" rel="noopener noreferrer" className="step-link radix-wallet-link">Get Radix Wallet</a>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <div className="step-content">
                            <h3>Fund Your Wallet</h3>
                            <p>Acquire XRD tokens through a supported exchange (like Bitfinex, MEXC, etc.) and transfer them to your Radix Wallet address.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                            <h3>Find the REDDICKS Validator</h3>
                            <p>Inside the Radix Wallet app, navigate to the 'Stake & Unstake' section. Use the search function to find the REDDICKS validator by name or paste our address:</p>
                            <code className="validator-code">{validatorAddress}</code>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">4</div>
                        <div className="step-content">
                            <h3>Delegate Your Stake</h3>
                            <p>Select the REDDICKS validator, enter the amount of XRD you wish to stake, and confirm the transaction. You'll start accumulating rewards right away!</p>
                             <a
                                href={`https://dashboard.radixdlt.com/network-staking/${validatorAddress}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cta-button validator-cta-small"
                            >
                                Stake via Explorer
                            </a>
                        </div>
                    </div>
                </div>
                 <p className="stake-note">Note: You need a small amount of XRD for transaction fees. Rewards typically take a couple of epochs (time periods on Radix) to start accumulating after staking.</p>
            </div>
        </section>

      </main>
      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Validator;
